import { FC, useEffect } from 'react';
import { useLazyGraphql } from 'utils/hooks/useLazyGraphql';
import { StateValueMap } from 'xstate';
import { useInterpret, useSelector } from '@xstate/react';
import {
  fetchPredictiveAudienceGoalsQuery,
  fetchPredictiveAudienceVocabularyQuery,
} from 'api/queries/predictiveAudiences';
import { CoreGoalsContext } from './CoreGoalsContext';
import {
  CoreGoalsContextStates,
  createCoreGoalStateMachineEffects,
  coreGoalsStateMachine,
} from './CoreGoalsContext.state';

const doesStateMatchValue = (stateKey: string, value: string, stateValue: StateValueMap) =>
  stateValue[stateKey] === value;

export const CoreGoalsContextProvider: FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  const { fetch: fetchVocab } = useLazyGraphql(fetchPredictiveAudienceVocabularyQuery, {
    fetchPolicy: 'network-only',
  });
  const { fetch: fetchGoals } = useLazyGraphql(fetchPredictiveAudienceGoalsQuery, {
    fetchPolicy: 'network-only',
  });
  const service = useInterpret(
    coreGoalsStateMachine,
    createCoreGoalStateMachineEffects({
      queryGoals: fetchGoals,
      queryVocab: fetchVocab,
    }),
  );

  useEffect(() => {
    service.send([
      CoreGoalsContextStates.goals.queryGoals,
      CoreGoalsContextStates.vocab.queryVocab,
    ]);
  }, []);

  const goals = useSelector(service, (state) => state.context.goals);
  const vocab = useSelector(service, (state) => state.context.vocab);

  const isLoading = useSelector(
    service,
    (state) =>
      doesStateMatchValue(
        CoreGoalsContextStates.goals.name,
        CoreGoalsContextStates.goals.queryGoals,
        state.value as StateValueMap,
      ) ||
      doesStateMatchValue(
        CoreGoalsContextStates.vocab.name,
        CoreGoalsContextStates.vocab.queryVocab,
        state.value as StateValueMap,
      ),
  );
  const isVocabError = useSelector(service, (state) =>
    doesStateMatchValue(
      CoreGoalsContextStates.vocab.name,
      CoreGoalsContextStates.vocab.vocabError,
      state.value as StateValueMap,
    ),
  );
  const isGoalError = useSelector(service, (state) =>
    doesStateMatchValue(
      CoreGoalsContextStates.goals.name,
      CoreGoalsContextStates.goals.goalsError,
      state.value as StateValueMap,
    ),
  );
  const areAllReady = useSelector(
    service,
    (state) =>
      (state.value as StateValueMap)[CoreGoalsContextStates.goals.name] ===
        CoreGoalsContextStates.goals.goalsComplete &&
      (state.value as StateValueMap)[CoreGoalsContextStates.vocab.name] ===
        CoreGoalsContextStates.vocab.vocabComplete,
  );
  return (
    <CoreGoalsContext.Provider
      value={{
        isLoading,
        areAllReady,
        isError: isVocabError || isGoalError,
        vocab: {
          vocabData: vocab,
          isError: isVocabError,
          retrigger: () => service.send(CoreGoalsContextStates.vocab.queryVocab),
        },
        goals: {
          goalData: goals,
          isError: isGoalError,
          retrigger: () => service.send(CoreGoalsContextStates.goals.queryGoals),
        },
      }}
    >
      {children}
    </CoreGoalsContext.Provider>
  );
};
